<template>
  <div class="route-steps">
    <div class="route-steps__inner d-flex align-center">
      <template v-for="queue in queues" :key="queue.queueNumber">
        <div class="route-step-stage d-flex align-center">
          <div
            v-if="queue.groups.length > 1"
            class="route-step-parallel d-flex flex-column">
            <div class="route-step-parallel__label d-flex align-center">
              <v-icon icon="mdi-drag-horizontal" size="x-small"></v-icon>
              Паралельно ({{ queue.groups.length }})
            </div>

            <button
              v-for="group in queue.groups"
              :key="group.id"
              :class="{ 'route-step--active': activeGroupId === group.id }"
              class="route-step route-step--compact d-flex align-center border-0"
              type="button"
              @click="emit('open-group', queue.queueNumber, group)">
              <span
                class="route-step__number d-flex align-center justify-center">
                {{ getGroupNumber(group) }}
              </span>
              <span class="route-step__title text-truncate">
                {{ group.name }}
              </span>
            </button>
          </div>

          <button
            v-else
            :class="{
              'route-step--active': activeGroupId === queue.groups[0].id,
            }"
            class="route-step d-flex align-center border-0"
            type="button"
            @click="emit('open-group', queue.queueNumber, queue.groups[0])">
            <span class="route-step__number d-flex align-center justify-center">
              {{ getGroupNumber(queue.groups[0]) }}
            </span>
            <span class="route-step__title text-truncate">
              {{ queue.groups[0].name }}
            </span>
          </button>

          <button
            v-tooltip="'Додати паралельну групу'"
            class="route-step-add d-flex align-center justify-center"
            type="button"
            @click.stop="emit('add-group', queue.queueNumber)">
            <v-icon icon="mdi-plus" size="small"></v-icon>
          </button>
        </div>

        <div
          v-if="queue.queueNumber !== lastQueueNumber"
          class="route-steps__divider">
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { TemplateRoute } from '@/requests/templateRoute'

defineOptions({
  name: 'RouteSteps',
})

const props = defineProps<{
  queues: Array<{
    queueNumber: number
    groups: TemplateRoute[]
  }>
  activeGroupId: number | null
}>()

const emit = defineEmits<{
  'open-group': [queueNumber: number, group: TemplateRoute]
  'add-group': [queueNumber: number]
}>()

const lastQueueNumber = computed(() => {
  return props.queues.at(-1)?.queueNumber
})

const groupNumbers = computed(() => {
  const numbers = new Map<number, number>()
  let number = 1

  props.queues.forEach(queue => {
    queue.groups.forEach(group => {
      numbers.set(group.id, number)
      number++
    })
  })

  return numbers
})

const getGroupNumber = (group: TemplateRoute) => {
  return groupNumbers.value.get(group.id)
}
</script>

<style lang="scss" scoped>
.route-steps {
  overflow-x: auto;
  overflow-y: hidden;
  padding: 14px;
  margin-bottom: 18px;
  background: #fafbfc;
  border: 1px solid #f0f0f5;
  border-radius: 11px;
}

.route-steps__inner {
  min-width: max-content;
  align-items: stretch;
}

.route-step-stage {
  flex-shrink: 0;
  align-self: center;
}

.route-step {
  flex-shrink: 0;
  align-self: center;
  gap: 9px;
  padding: 8px 12px;
  border-radius: 9px;
  background: transparent;
  cursor: pointer;
  transition: background 0.12s;
}

.route-step--active {
  background: #e6f1fb;
}

.route-step__number {
  flex: 0 0 26px;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #e8e8ed;
  color: #9898a8;
  font-size: 12px;
  font-weight: 700;
}

.route-step--active .route-step__number {
  background: #185fa5;
  color: #fff;
}

.route-step__title {
  min-width: 0;
  max-width: 160px;
  color: #6c6c7d;
  font-weight: 500;
  white-space: nowrap;
}

.route-step--active .route-step__title {
  color: #185fa5;
  font-weight: 600;
}

.route-step--compact {
  gap: 8px;
  padding: 6px 10px;
  border: 1px solid #f0f0f5 !important;
  border-radius: 8px;
  background: #fff;
}

.route-step--compact.route-step--active {
  border-color: #b8d4ec !important;
}

.route-step--compact .route-step__number {
  flex-basis: 22px;
  width: 22px;
  height: 22px;
  font-size: 11px;
}

.route-step--compact .route-step__title {
  max-width: 150px;
  font-size: 13px;
}

.route-step-parallel {
  flex-shrink: 0;
  align-self: center;
  gap: 6px;
  padding: 8px 10px;
  border: 1.5px dashed #d8c0f0;
  border-radius: 10px;
  background: #faf6fe;
}

.route-step-parallel__label {
  gap: 4px;
  color: #7c3aed;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.route-steps__divider {
  flex-shrink: 0;
  align-self: center;
  width: 20px;
  height: 2px;
  background: #e8e8ed;
}

.route-step-add {
  flex-shrink: 0;
  align-self: center;
  width: 30px;
  height: 30px;
  margin: 0 8px;
  border: 1.5px dashed #c5a8e8;
  border-radius: 50%;
  background: #fff;
  color: #7c3aed;
  cursor: pointer;
  transition:
    background 0.12s,
    border-color 0.12s;
}

.route-step-add:hover {
  border-color: #7c3aed;
  background: #faf6fe;
}
</style>
